import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import posthog from 'posthog-js';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Erreur non gérée:', error, errorInfo);

    // Capture error in PostHog for monitoring
    if (posthog.__loaded) {
      posthog.captureException(error, {
        $exception_message: error.message,
        $exception_type: error.name,
        $exception_stack_trace_raw: error.stack,
        componentStack: errorInfo.componentStack,
        context: 'ErrorBoundary',
      });
    }
  }

  private handleReset = () => {
    this.setState({ hasError: false, error: null });
    window.location.href = '/';
  };

  private handleReload = () => {
    window.location.reload();
  };

  public render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-[#F8F9FA] flex items-center justify-center p-6">
          <Card className="max-w-lg w-full border-[#FF7171] border-2 rounded-xl shadow-lg">
            <CardContent className="p-8">
              <div className="flex flex-col items-center text-center space-y-6">
                {/* Icon */}
                <div className="w-16 h-16 rounded-full bg-[#FF7171]/10 flex items-center justify-center">
                  <AlertTriangle className="h-8 w-8 text-[#FF7171]" />
                </div>

                {/* Titre */}
                <div className="space-y-2">
                  <h1 className="text-2xl font-semibold text-[#37699E] font-['Lexend_Deca']">
                    Une erreur est survenue
                  </h1>
                  <p className="text-[#5A6B7A] font-['Funnel_Sans']">
                    Nous sommes désolés, quelque chose s'est mal passé. Veuillez réessayer.
                  </p>
                </div>

                {/* Détails de l'erreur (en dev) */}
                {import.meta.env.DEV && this.state.error && (
                  <div className="w-full p-4 bg-[#EBF4F8] rounded-lg text-left">
                    <p className="text-xs font-mono text-[#2D3748] break-all">
                      {this.state.error.toString()}
                    </p>
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-3 w-full">
                  <Button
                    onClick={this.handleReload}
                    variant="outline"
                    className="flex-1 border-[#DBE2EA] text-[#5A6B7A] hover:bg-[#EBF4F8] font-['Funnel_Sans']"
                  >
                    <RefreshCw className="mr-2 h-4 w-4" />
                    Recharger
                  </Button>
                  <Button
                    onClick={this.handleReset}
                    className="flex-1 bg-[#FFBF07] hover:bg-[#E5AB06] text-[#2D3748] font-semibold"
                  >
                    Retour à l'accueil
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}
